import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Calendar,
  Trash2,
  RefreshCcw,
  CheckCircle2,
  Loader2,
  Inbox,
} from "lucide-react";

const STATUS_OPTIONS = [
  { v: "new", label: "Nowy", color: "text-stormly-cyan border-stormly-cyan/40 bg-stormly-cyan/5" },
  { v: "contacted", label: "Kontakt", color: "text-yellow-300 border-yellow-300/40 bg-yellow-300/5" },
  { v: "won", label: "Zamknięty +", color: "text-green-400 border-green-400/40 bg-green-400/5" },
  { v: "lost", label: "Stracony", color: "text-red-400 border-red-400/40 bg-red-400/5" },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (loading) return;
    if (!user) nav("/login", { replace: true });
  }, [user, loading, nav]);

  const load = async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (err) {
      toast.error("Nie udało się pobrać zgłoszeń");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line
  }, [user]);

  const onDelete = async (id) => {
    if (!window.confirm("Usunąć zgłoszenie?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((l) => l.filter((x) => x.id !== id));
      toast.success("Usunięto");
    } catch {
      toast.error("Błąd usuwania");
    }
  };

  const onStatusChange = async (id, status) => {
    try {
      await api.patch(`/leads/${id}/status`, { status });
      setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch {
      toast.error("Błąd zmiany statusu");
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1A2E]">
        <Loader2 className="animate-spin text-stormly-cyan" size={32} />
      </div>
    );
  }

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const stats = {
    total: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    won: leads.filter((l) => l.status === "won").length,
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#1A1A2E] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#1A1A2E]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-heading font-black text-xl">
              Stormly
            </Link>
            <span className="text-white/30">/</span>
            <span className="text-white/70 font-medium">Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-white/60">
              Zalogowany jako{" "}
              <span className="text-stormly-cyan font-semibold">
                {user.username}
              </span>
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-white/15 hover:border-stormly-cyan/40 hover:text-stormly-cyan transition-colors"
            >
              <LogOut size={14} /> Wyloguj
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Wszystkie", val: stats.total, c: "text-white" },
            { label: "Nowe", val: stats.new, c: "text-stormly-cyan" },
            { label: "Kontakt", val: stats.contacted, c: "text-yellow-300" },
            { label: "Zamknięte +", val: stats.won, c: "text-green-400" },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-[#1F1F38] border border-white/10 rounded-2xl p-5"
            >
              <p className="text-xs uppercase tracking-widest text-white/50">
                {s.label}
              </p>
              <p className={`font-heading font-black text-3xl mt-2 ${s.c}`}>
                {s.val}
              </p>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter("all")}
              data-testid="filter-all"
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                filter === "all"
                  ? "bg-stormly-cyan text-[#0a0a1e] border-stormly-cyan font-semibold"
                  : "border-white/15 text-white/70 hover:border-white/30"
              }`}
            >
              Wszystkie
            </button>
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.v}
                onClick={() => setFilter(s.v)}
                data-testid={`filter-${s.v}`}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  filter === s.v
                    ? "bg-stormly-cyan text-[#0a0a1e] border-stormly-cyan font-semibold"
                    : "border-white/15 text-white/70 hover:border-white/30"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={fetching}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-white/15 hover:border-stormly-cyan/40 transition-colors self-start"
          >
            <RefreshCcw size={14} className={fetching ? "animate-spin" : ""} />
            Odśwież
          </button>
        </div>

        {/* Leads list */}
        {fetching ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-stormly-cyan" size={28} />
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="admin-empty"
            className="text-center py-20 bg-[#1F1F38] border border-white/10 rounded-3xl"
          >
            <Inbox size={36} className="mx-auto text-white/30 mb-4" />
            <p className="text-white/60">Brak zgłoszeń w tym widoku.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((lead, i) => (
              <article
                key={lead.id}
                data-testid={`lead-card-${i}`}
                className="bg-[#1F1F38] border border-white/10 rounded-2xl p-5 md:p-6 hover:border-stormly-cyan/30 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h3 className="font-heading text-lg font-bold">
                        {lead.name}
                      </h3>
                      <select
                        value={lead.status}
                        onChange={(e) => onStatusChange(lead.id, e.target.value)}
                        data-testid={`lead-status-${i}`}
                        className={`text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full border bg-transparent cursor-pointer focus:outline-none ${
                          STATUS_OPTIONS.find((s) => s.v === lead.status)?.color
                        }`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option
                            key={s.v}
                            value={s.v}
                            className="bg-[#1F1F38] text-white"
                          >
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap text-sm text-white/60 mb-3">
                      <a
                        href={`mailto:${lead.email}`}
                        className="inline-flex items-center gap-1.5 hover:text-stormly-cyan"
                      >
                        <Mail size={13} /> {lead.email}
                      </a>
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar size={13} />
                        {new Date(lead.created_at).toLocaleString("pl-PL")}
                      </span>
                    </div>
                    <p className="text-white/80 text-sm leading-relaxed whitespace-pre-line">
                      {lead.description}
                    </p>
                  </div>
                  <div className="flex md:flex-col gap-2 shrink-0">
                    <a
                      href={`mailto:${lead.email}`}
                      className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-stormly-cyan/10 border border-stormly-cyan/30 text-stormly-cyan hover:bg-stormly-cyan/20 transition-colors"
                    >
                      <CheckCircle2 size={13} /> Odpisz
                    </a>
                    <button
                      onClick={() => onDelete(lead.id)}
                      data-testid={`lead-delete-${i}`}
                      className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border border-white/15 text-white/70 hover:border-red-400/40 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={13} /> Usuń
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
